import { Button, Card, Empty, Input, Select, Space, Table, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApplicationRow = {
  id: string;
  first_name: string;
  last_name?: string;
  status: string;
  grade_level_id: string;
  session_id: string;
  assigned_to?: string | null;
  admission_no?: string | null;
  campus_name?: string | null;
  campus_code?: string | null;
  session_name?: string | null;
  session_code?: string | null;
  grade_name?: string | null;
  grade_code?: string | null;
  category?: string | null;
  guardian_name?: string | null;
};

const STATUSES = [
  'draft',
  'submitted',
  'verification',
  'assessment',
  'decision_pending',
  'approved',
  'waitlisted',
  'rejected',
  'offer_issued',
  'accepted',
  'converted',
  'deferred',
  'cancelled',
];

export default function AdmissionsListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const userId = useAuthStore((state) => state.user?.id);
  const status = params.get('status') || undefined;
  const grade = params.get('grade') || undefined;
  const session = params.get('session') || undefined;
  const q = params.get('q') || undefined;
  const assigned = params.get('assigned') || undefined;

  const query = useQuery({
    queryKey: ['education-admissions', status, grade, session, q, assigned],
    queryFn: () =>
      api.get<ApplicationRow[]>('/education/admissions', {
        params: {
          status,
          grade_level_id: grade,
          session_id: session,
          q,
          assigned_to: assigned === 'me' ? userId : undefined,
        },
      }),
  });

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const rows = query.data?.data ?? [];

  const canDecide = Boolean(useAuthStore((state) => state.user?.permissions?.includes('education.admission.decide')));
  const queryClient = useQueryClient();
  const waitlist = useQuery({
    queryKey: ['education-admission-waitlist', session, grade],
    enabled: status === 'waitlisted',
    queryFn: () =>
      api.get<Array<{ id: string; waitlist_rank?: number | null }>>('/education/admissions/waitlist', {
        params: { session_id: session, grade_level_id: grade },
      }),
  });
  const rankById = new Map((waitlist.data?.data ?? []).map((row) => [row.id, row.waitlist_rank ?? null]));
  const promote = useMutation({
    mutationFn: () =>
      api.post<{ promoted: boolean; reason?: string }>('/education/admissions/waitlist/promote', {
        session_id: session,
        grade_level_id: grade,
      }),
    onSuccess: (res) => {
      if (res.data?.promoted) message.success(t('waitlistPromoted', { defaultValue: 'Offer issued to the next waitlisted applicant' }));
      else if (res.data?.reason === 'no_seat') message.warning(t('waitlistNoSeat', { defaultValue: 'No seat is available for this grade and session' }));
      else message.info(t('waitlistEmpty', { defaultValue: 'The waitlist is empty' }));
      queryClient.invalidateQueries({ queryKey: ['education-admissions'] });
      queryClient.invalidateQueries({ queryKey: ['education-admission-waitlist'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('admissionsTitle')}
      description={t('admissionsCbseIntro', {
        defaultValue:
          'India CBSE private-school admission register. Capture enquiry → application → documents → assessment → offer. Fees and exams are out of scope for this release.',
      })}
      tags={<Tag>{rows.length} {t('admissionsTitle')}</Tag>}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/admissions/enquiries')}>
            {t('enquiriesTitle', { defaultValue: 'Admission enquiries' })}
          </Button>
          <Button onClick={() => navigate(`/education/admissions/crm?${params.toString()}`)}>
            {t('admissionsCrm')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/education/admissions/new?${params.toString()}`)}>
            {t('newApplication')}
          </Button>
        </Space>
      }
    >
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t('search')}
              defaultValue={q}
              onSearch={(value) => setFilter('q', value || undefined)}
              style={{ width: 240 }}
            />
            <Select
              allowClear
              placeholder={t('status')}
              value={status}
              style={{ minWidth: 180 }}
              options={STATUSES.map((item) => ({ value: item, label: item }))}
              onChange={(value) => setFilter('status', value)}
            />
            <EducationGradeSelect
              placeholder={t('grade')}
              value={grade}
              onChange={(value) => setFilter('grade', value || undefined)}
              style={{ width: 200 }}
            />
            <EducationSessionSelect
              placeholder={t('session')}
              value={session}
              onChange={(value) => setFilter('session', value || undefined)}
              style={{ width: 220 }}
            />
            <Button
              type={assigned === 'me' ? 'primary' : 'default'}
              onClick={() => setFilter('assigned', assigned === 'me' ? undefined : 'me')}
            >
              {t('mine')}
            </Button>
            {status === 'waitlisted' && canDecide ? (
              <Button
                type="primary"
                disabled={!grade || !session}
                loading={promote.isPending}
                title={t('waitlistPromoteHint', { defaultValue: 'Choose a session and grade, then offer the freed seat to the first applicant in the queue' })}
                onClick={() => promote.mutate()}
              >
                {t('waitlistPromote', { defaultValue: 'Promote next from waitlist' })}
              </Button>
            ) : null}
          </Space>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('hubQueueEmptyAdmissions')}>
                  <Button type="primary" onClick={() => navigate(`/education/admissions/new?${params.toString()}`)}>
                    {t('newApplication')}
                  </Button>
                </Empty>
              ),
            }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/admissions/${row.id}?${params.toString()}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              ...(status === 'waitlisted'
                ? [
                    {
                      title: t('waitlistRank', { defaultValue: 'Queue #' }),
                      width: 90,
                      render: (_v: unknown, row: ApplicationRow) => rankById.get(row.id) ?? '—',
                    },
                  ]
                : []),
              {
                title: t('applicant'),
                render: (_v, row) => `${row.first_name} ${row.last_name || ''}`.trim(),
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (value: string) => <Tag>{value}</Tag>,
              },
              {
                title: t('campus'),
                width: 140,
                ellipsis: true,
                render: (_v, row) => row.campus_name || row.campus_code || '—',
              },
              {
                title: t('grade'),
                width: 120,
                ellipsis: true,
                render: (_v, row) => row.grade_name || row.grade_code || '—',
              },
              {
                title: t('session'),
                width: 140,
                ellipsis: true,
                render: (_v, row) => row.session_name || row.session_code || '—',
              },
              {
                title: t('category', { defaultValue: 'Category' }),
                dataIndex: 'category',
                width: 100,
                render: (value: string | null) => value || '—',
              },
              {
                title: t('guardianName'),
                dataIndex: 'guardian_name',
                ellipsis: true,
                render: (value: string | null) => value || '—',
              },
              {
                title: t('assignedTo'),
                dataIndex: 'assigned_to',
                width: 100,
                render: (value: string | null) => (value && value === userId ? t('mine') : value || '—'),
              },
            ]}
          />
        </Space>
      </Card>
    </EducationPageChrome>
  );
}
